/**
 * Job Sites (Customer Portal app) — ADMIN-ONLY (owner directive 2026-09-29).
 *
 * The JobBot "Job Sites" manager was moved out of the Job Finder module and
 * into the Customer Portal. This is the ADMIN view: the owner maintains the
 * single, PRE-CONFIGURED system list of job boards that every user's agents
 * draw from. End users no longer add or manage sites.
 *
 * Kept: the URL compatibility check (four-tier verdict), the per-site
 * "how to apply" recipe, and the review queue for any legacy user-site
 * submissions awaiting accept/decline.
 */
import { useState, useEffect, FormEvent } from 'react';
import {
  Plus, Globe, Trash2, Pencil, AlertCircle, Power, ShieldCheck, CheckCircle2,
  XCircle, Loader2,
} from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import { useConfirm } from '../../components/ConfirmDialog';
import { jobbotApi, JobSite, SiteCompatibilityVerdict } from '../../api/jobbot';
import SiteAuthSummaryCard from '../../components/SiteAuthSummaryCard';
import SiteAuthList from '../../components/SiteAuthList';
import VerificationQueueCard from '../../components/VerificationQueueCard';
import PluginRetirementCard from '../../components/PluginRetirementCard';
import GoLiveReadinessCard from '../../components/GoLiveReadinessCard';
import { errText } from '../../utils/errors';
import { timeAgoWithDate } from '../../utils/formatTime';
import EntityDetailModal from '../../components/EntityDetailModal';
import { cardOpen } from '../../utils/cardOpen';

/** Human tone per compatibility tier. */
const TIER_TONE: Record<string, string> = {
  full_agent: 'bg-green-500/15 text-green-700 dark:text-green-400',
  partial_plugin: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  partial_manual: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  incompatible: 'bg-red-500/15 text-red-700 dark:text-red-400',
};
const TIER_LABEL: Record<string, string> = {
  full_agent: 'Fully compatible',
  partial_plugin: 'Needs verification',
  partial_manual: 'Manual apply',
  incompatible: 'Not compatible',
};

export default function JobSitesApp() {
  const confirm = useConfirm();

  const [sites, setSites] = useState<JobSite[]>([]);
  const [detailSite, setDetailSite] = useState<JobSite | null>(null);
  const [pending, setPending] = useState<JobSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<JobSite | null>(null);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [category, setCategory] = useState('');
  const [instructions, setInstructions] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [listError, setListError] = useState('');
  const [notice, setNotice] = useState('');
  const [verdict, setVerdict] = useState<SiteCompatibilityVerdict | null>(null);
  const [checking, setChecking] = useState(false);

  const fetchSites = async () => {
    setLoading(true);
    try {
      const res = await jobbotApi.sites.list();
      setSites((res.data || []).filter(s => s.scope === 'system'));
      setListError('');
      try {
        const pr = await jobbotApi.sites.pendingReview();
        setPending(pr.data || []);
      } catch { setPending([]); }
    } catch (err: any) {
      setListError(errText(err, 'Could not load the job sites.'));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchSites(); }, []); // eslint-disable-line

  const openCreate = () => {
    setEditing(null); setName(''); setUrl(''); setSearchQuery(''); setIsActive(true);
    setCategory(''); setInstructions(''); setError(''); setVerdict(null); setShowModal(true);
  };

  const openEdit = (site: JobSite) => {
    setEditing(site); setName(site.name); setUrl(site.url || ''); setSearchQuery(site.search_query || '');
    setIsActive(site.is_active === 'true'); setCategory(site.category || '');
    setInstructions(site.submission_instructions || '');
    setError(''); setVerdict(null); setShowModal(true);
  };

  const handleCheck = async () => {
    const target = url.trim();
    if (!target) { setVerdict(null); return; }
    setChecking(true);
    try {
      const res = await jobbotApi.sites.check(target);
      setVerdict(res.data);
      if (!name.trim() && res.data.url) {
        try { setName(new URL(res.data.url).hostname.replace(/^www\./, '')); } catch { /* ignore */ }
      }
    } catch (err: any) {
      setError(errText(err, 'Could not check that URL.'));
    } finally { setChecking(false); }
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      if (editing) {
        await jobbotApi.sites.update(editing.id, { name, url, search_query: searchQuery, is_active: isActive, category, submission_instructions: instructions });
      } else {
        await jobbotApi.sites.create({
          name, url, search_query: searchQuery, is_active: isActive,
          category: category || undefined,
          submission_instructions: instructions || undefined,
          scope: 'system',
        });
      }
      setShowModal(false); fetchSites();
    } catch (err: any) { setError(errText(err, 'Failed to save')); } finally { setSaving(false); }
  };

  const handleDelete = async (id: string, siteName: string) => {
    const choice = await confirm({
      title: 'Delete this job site?',
      message: `“${siteName}” will be removed from the system list permanently. This cannot be undone.`,
      actions: [
        { label: 'Cancel', value: false, tone: 'secondary' },
        { label: 'Delete', value: 'delete', tone: 'danger' },
      ],
    });
    if (choice !== 'delete') return;
    setListError('');
    try { await jobbotApi.sites.delete(id); fetchSites(); } catch (err: any) {
      setListError(errText(err, 'The job site could not be deleted.'));
    }
  };

  const toggleActive = async (site: JobSite) => {
    setListError('');
    try { await jobbotApi.sites.update(site.id, { is_active: site.is_active !== 'true' }); fetchSites(); } catch (err: any) {
      setListError(errText(err, 'The job site could not be updated.'));
    }
  };

  const decide = async (site: JobSite, decision: 'accept' | 'decline') => {
    const choice = await confirm({
      title: decision === 'accept' ? 'Accept this site?' : 'Decline this site?',
      message: decision === 'accept'
        ? `“${site.name}” will be added to the SYSTEM list for everyone.`
        : `“${site.name}” will NOT be added to the system list. The user keeps it privately.`,
      actions: [
        { label: 'Cancel', value: false, tone: 'secondary' },
        { label: decision === 'accept' ? 'Accept' : 'Decline', value: 'go', tone: decision === 'accept' ? 'primary' : 'danger' },
      ],
    });
    if (choice !== 'go') return;
    setNotice(''); setListError('');
    try {
      await jobbotApi.sites.review(site.id, decision);
      setNotice(decision === 'accept' ? `“${site.name}” accepted into the system list.` : `“${site.name}” declined.`);
      fetchSites();
    } catch (err: any) { setListError(errText(err, 'The decision could not be saved.')); }
  };

  if (loading) {
    return <div className="flex h-full items-center justify-center"><LoadingSpinner size="lg" label="Loading sites..." /></div>;
  }

  const renderCard = (site: JobSite) => (
    <Card key={site.id} hover className="flex min-w-0 cursor-pointer flex-col" {...cardOpen({ agentId: `portal.jobsites.card.${site.id}`, label: `Open details for ${site.name}`, onOpen: () => setDetailSite(site) })}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-500/20 text-blue-700 dark:text-blue-400"><Globe size={20} aria-hidden="true" /></div>
          <div className="min-w-0">
            <h3 className="truncate font-medium text-slate-950 dark:text-slate-100" title={site.name}>{site.name}</h3>
            <div className="mt-1 flex flex-wrap items-center gap-1">
              <span className={`inline-block rounded-md px-2 py-0.5 text-xs ${site.is_active === 'true' ? 'bg-green-500/20 text-green-700 dark:text-green-400' : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-400'}`}>
                {site.is_active === 'true' ? 'Active' : 'Inactive'}
              </span>
              {site.compatibility && (
                <span className={`inline-block rounded-md px-2 py-0.5 text-xs ${TIER_TONE[site.compatibility] || ''}`} data-agent-id={`portal.jobsites.compat.${site.id}`}>
                  {TIER_LABEL[site.compatibility] || site.compatibility}
                </span>
              )}
              {site.category && (
                <span className="inline-block rounded-md bg-brand-500/15 px-2 py-0.5 text-xs text-brand-700 dark:text-brand-300">
                  {site.category.split('|')[0]}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); void toggleActive(site); }} className="rounded-lg p-2 text-slate-700 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500" title="Toggle active" aria-label="Toggle active"><Power size={16} aria-hidden="true" /></button>
          <button onClick={(e) => { e.stopPropagation(); openEdit(site); }} className="rounded-lg p-2 text-slate-700 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500" aria-label="Edit"><Pencil size={16} aria-hidden="true" /></button>
          <button onClick={(e) => { e.stopPropagation(); void handleDelete(site.id, site.name); }} className="rounded-lg p-2 text-slate-700 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-red-700 dark:hover:text-red-400 focus:outline-none focus:ring-2 focus:ring-brand-500" aria-label="Delete"><Trash2 size={16} aria-hidden="true" /></button>
        </div>
      </div>
      {site.url && <a href={site.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="mt-2 block truncate text-sm text-brand-700 dark:text-brand-400 hover:text-brand-300" title={site.url}>{site.url}</a>}
      {site.search_query && <p className="mt-1 truncate text-xs text-slate-600 dark:text-slate-500" title={site.search_query}>Query: {site.search_query}</p>}
      {site.submission_instructions && (
        <div className="mt-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-2.5 py-2" data-agent-id={`portal.jobsites.instructions.view.${site.id}`}>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">How to apply</p>
          <p className="mt-0.5 whitespace-pre-wrap text-xs text-slate-700 dark:text-slate-300">{site.submission_instructions}</p>
        </div>
      )}
    </Card>
  );

  return (
    <div className="min-w-0">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-slate-950 dark:text-slate-100">Job Sites</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">The pre-configured job boards every user's agents draw from. Admin only.</p>
        </div>
        <Button onClick={openCreate} data-agent-id="portal.jobsites.new">
          <Plus size={18} aria-hidden="true" />New Site
        </Button>
      </div>

      {/* Read-only, flag-gated auth summary. Renders nothing while the backend
          flag SAIK_SITE_STATUS_BOARD_ENABLED is OFF (or on error/timeout). */}
      <SiteAuthSummaryCard />

      {/* Read-only, flag-gated per-site sign-in list. Also renders nothing while
          the flag is OFF (or on error/timeout/empty). */}
      <SiteAuthList />

      {/* Read-only, flag-gated guided sign-in verification queue. Renders nothing
          while the backend flag SAIK_GUIDED_VERIFY_ENABLED is OFF (or on error/timeout/empty). */}
      <VerificationQueueCard />

      {/* Read-only, flag-gated plug-in retirement readiness (Phase 4 prep). Renders
          nothing while BOTH backend flags are OFF (or on error/timeout). */}
      <PluginRetirementCard />

      {/* Read-only, flag-gated go-live readiness report (increment #24). Renders
          nothing while SAIK_GO_LIVE_READINESS_ENABLED is OFF (or on error/timeout). */}
      <GoLiveReadinessCard />

      {listError && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2.5 text-sm text-red-700 dark:text-red-400" role="alert" data-agent-id="portal.jobsites.error">
          <AlertCircle size={16} aria-hidden="true" />{listError}
        </div>
      )}
      {notice && (
        <div className="mb-4 rounded-lg border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/30 px-3 py-2.5 text-sm text-green-700 dark:text-green-400" role="status" data-agent-id="portal.jobsites.notice">{notice}</div>
      )}

      {pending.length > 0 && (
        <Card className="mb-6 p-4" data-agent-id="portal.jobsites.review.queue">
          <div className="mb-3 flex items-center gap-2">
            <ShieldCheck size={18} className="text-brand-600" aria-hidden="true" />
            <h2 className="font-semibold text-slate-950 dark:text-slate-100">Sites awaiting your review</h2>
            <span className="rounded-md bg-slate-100 dark:bg-slate-700/50 px-2 py-0.5 text-xs text-slate-700 dark:text-slate-300">{pending.length}</span>
          </div>
          <div className="space-y-2">
            {pending.map(site => (
              <div key={site.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3" data-agent-id={`portal.jobsites.review.${site.id}`}>
                <div className="min-w-0">
                  <p className="truncate font-medium text-slate-950 dark:text-slate-100">{site.name}</p>
                  {site.url && <p className="truncate text-xs text-slate-600 dark:text-slate-500">{site.url}</p>}
                  {site.compatibility && <span className={`mt-1 inline-block rounded-md px-2 py-0.5 text-xs ${TIER_TONE[site.compatibility] || ''}`}>{TIER_LABEL[site.compatibility] || site.compatibility}</span>}
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button size="sm" onClick={() => decide(site, 'accept')} data-agent-id={`portal.jobsites.review.accept.${site.id}`}><CheckCircle2 size={14} aria-hidden="true" />Accept</Button>
                  <Button size="sm" variant="secondary" onClick={() => decide(site, 'decline')} data-agent-id={`portal.jobsites.review.decline.${site.id}`}><XCircle size={14} aria-hidden="true" />Decline</Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {sites.length === 0 ? (
        <EmptyState
          icon={<Globe size={28} aria-hidden="true" />}
          title="No job sites yet"
          description="Add a system job site to start collecting leads."
          action={<Button onClick={openCreate}><Plus size={18} aria-hidden="true" />New Site</Button>}
        />
      ) : (
        <>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-500" data-agent-id="portal.jobsites.section.system">System sites ({sites.length})</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{sites.map(renderCard)}</div>
        </>
      )}

      <Modal open={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit Site' : 'New System Site'} size="lg">
        <form onSubmit={handleSave}>
          {error && <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2.5 text-sm text-red-700 dark:text-red-400" role="alert"><AlertCircle size={16} aria-hidden="true" />{error}</div>}

          {!editing && (
            <p className="mb-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-3 py-2 text-xs text-slate-700 dark:text-slate-300">
              Paste the job board or company URL. We will check what the system can do with it —
              full auto-apply, needs verification, manual, or not compatible.
            </p>
          )}

          <div className="mb-4">
            <label className="mb-1.5 block text-sm font-medium text-slate-800 dark:text-slate-300">URL (job board or company)</label>
            <input type="text" value={url} onChange={e => { setUrl(e.target.value); setVerdict(null); }} onBlur={handleCheck}
              data-agent-id="portal.jobsites.form.url"
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm text-slate-950 dark:text-slate-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="https://boards.greenhouse.io/company" />
            {checking && (
              <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-500" role="status">
                <Loader2 size={12} className="animate-spin" aria-hidden="true" />Checking compatibility…
              </p>
            )}
            {verdict && !checking && (
              <div className={`mt-2 rounded-lg px-3 py-2 text-xs ${TIER_TONE[verdict.tier] || ''}`} role="status" data-agent-id="portal.jobsites.form.verdict">
                <span className="font-semibold">{TIER_LABEL[verdict.tier] || verdict.tier}</span> — {verdict.label}
                <div className="mt-0.5 opacity-90">{verdict.detail}</div>
                {verdict.error && <div className="mt-0.5 opacity-90">Check error: {verdict.error}</div>}
              </div>
            )}
          </div>

          <div className="mb-4">
            <label className="mb-1.5 block text-sm font-medium text-slate-800 dark:text-slate-300">Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} required
              data-agent-id="portal.jobsites.form.name"
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm text-slate-950 dark:text-slate-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="Acme Corp (Greenhouse)" />
          </div>

          <div className="mb-4">
            <label className="mb-1.5 block text-sm font-medium text-slate-800 dark:text-slate-300">Search Query</label>
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              data-agent-id="portal.jobsites.form.query"
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm text-slate-950 dark:text-slate-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="remote python developer" />
          </div>

          <div className="mb-4">
            <label className="mb-1.5 block text-sm font-medium text-slate-800 dark:text-slate-300">Category</label>
            <input type="text" value={category.split('|')[0]} onChange={e => setCategory(e.target.value)}
              data-agent-id="portal.jobsites.form.category"
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm text-slate-950 dark:text-slate-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="e.g. Technology & Software" />
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-500">Derived from the company name. Edit it to override.</p>
          </div>

          <div className="mb-4">
            <label htmlFor="submissionInstructions" data-agent-id="portal.jobsites.instructions.edit" className="mb-1.5 block text-sm font-medium text-slate-800 dark:text-slate-300">How to submit an application</label>
            <textarea id="submissionInstructions" value={instructions} onChange={e => setInstructions(e.target.value)} rows={5}
              data-agent-id="portal.jobsites.instructions.text"
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm text-slate-950 dark:text-slate-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder={'Site-specific recipe the agent follows at submit time.\nE.g. "Click Apply, choose “Upload resume” (not LinkedIn import), skip the salary box, then confirm the email link."'} />
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-500">Steps, caveats we hit ourselves, and workarounds. The agent reads this at submit time. Leave blank if there is nothing special.</p>
          </div>

          <div className="mb-6 flex items-center gap-2">
            <input type="checkbox" id="isActive" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="rounded border-slate-600 bg-white dark:bg-slate-900 text-brand-600 focus:ring-brand-500" />
            <label htmlFor="isActive" className="text-sm text-slate-800 dark:text-slate-300">Active</label>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-agent-id="portal.jobsites.form.submit">{saving ? <LoadingSpinner size="sm" /> : null}{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        </form>
      </Modal>

      <EntityDetailModal
        open={!!detailSite}
        onClose={() => setDetailSite(null)}
        title={detailSite ? detailSite.name : 'Job site'}
        rows={detailSite ? [
          { label: 'Name', value: detailSite.name },
          { label: 'URL', value: detailSite.url ? <a href={detailSite.url} target="_blank" rel="noreferrer" className="break-all text-brand-700 hover:underline dark:text-brand-400">{detailSite.url}</a> : '—' },
          { label: 'Search query', value: detailSite.search_query || '—' },
          { label: 'Category', value: detailSite.category ? detailSite.category.split('|')[0] : '—' },
          { label: 'Compatibility', value: detailSite.compatibility ? (TIER_LABEL[detailSite.compatibility] || detailSite.compatibility) : '—' },
          { label: 'Approval', value: detailSite.approval_status || '—' },
          { label: 'Status', value: detailSite.is_active === 'true' ? 'Active' : 'Inactive' },
          { label: 'How to apply', value: detailSite.submission_instructions ? <span className="whitespace-pre-wrap break-words">{detailSite.submission_instructions}</span> : '—' },
          { label: 'Created', value: timeAgoWithDate(detailSite.created_at) },
        ] : []}
      />
    </div>
  );
}
